import React, { FC, useState, useRef, useMemo, useCallback } from 'react';
import {
    Container,
    Title,
    TitleContainer,
    DescriptionContainer,
    Description,
    ModalTitle,
    DirectionDescription,
    ModalContainer,
    DirectionsWrapper,
    Bubble5,
    Bubble6,
    DesktopDirectionsContainer,
    MobileDirectionsContainer,
    DirectionsGrid,
    AccordionDirectionsGrid,
} from './styled';
import { BaseWrapper } from 'styles/common';
import { sanitize } from 'isomorphic-dompurify';
import { Direction, Type } from 'types';
import DirectionBlock from './DirectionBlock';
import { ModalElement, IAccordionItem } from 'kl-b2c-ui-kit';
import PrimaryModal from 'components/modal/PrimaryModal';
import Tab from 'components/tab';
import { useTranslation } from 'react-i18next';
import DirectionTeam from './DirectionTeam';
import { useDirections } from 'contexts/directions.context';
import PrimaryAccordion from 'components/accordion/PrimaryAccordion';

interface DirectionsProps {
    config: {
        title: string;
        description: string;
        isClosed?: boolean;
    };
}

const Directions: FC<DirectionsProps> = ({ config: { title, description, isClosed } }) => {
    const [activeDirection, setActiveDirection] = useState<Direction>();
    const modalRef = useRef<ModalElement>(null);
    const { t } = useTranslation('pages/home');
    const { directions } = useDirections();

    const groupedDirections = useMemo(() => {
        if (!directions) return [];
        const map = new Map<string, { type: Type; items: Direction[] }>();

        directions.forEach((d) => {
            const key = d.type.id;
            let group = map.get(key);
            if (!group) {
                group = { type: d.type, items: [] };
                map.set(key, group);
            }
            group.items.push(d);
        });

        return Array.from(map.values());
    }, [directions]);

    const handleDirectionClick = useCallback((d: Direction) => {
        setActiveDirection(d);
        modalRef.current?.open();
    }, []);

    const mobileAccordionItems: IAccordionItem[] = useMemo(
        () =>
            groupedDirections.map((group) => ({
                key: group.type.id,
                title: <span>{group.type.name}</span>,
                body: (
                    <AccordionDirectionsGrid>
                        {group.items.map((d) => (
                            <DirectionBlock
                                key={d.id}
                                item={d}
                                isClosed={!!isClosed}
                                click={() => handleDirectionClick(d)}
                                isMobileAccordion
                            />
                        ))}
                    </AccordionDirectionsGrid>
                ),
            })),
        [groupedDirections, isClosed, handleDirectionClick]
    );

    const getTabs = () => ({
        tabs: [
            {
                key: 'description',
                isActive: true,
                body: (
                    <DirectionDescription data-at-selector={'directionsModalTabMenuBodyDescription'}>
                        <div
                            data-at-selector={'directionsModalTabMenuBodyDescriptionContent'}
                            dangerouslySetInnerHTML={{
                                __html: sanitize(activeDirection?.description || ''),
                            }}
                        />

                        {activeDirection?.link && (
                            <a
                                data-at-selector={'directionsModalTabMenuBodyDescriptionLink'}
                                href={activeDirection.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('show-more')}
                            </a>
                        )}
                    </DirectionDescription>
                ),
                title: <span>{t('direction-description')}</span>,
            },
            {
                key: 'teams',
                body: (
                    <>
                        <DirectionDescription data-at-selector={'directionsModalTabMenuBodyTeamsDescription'}>
                            {t('direction-subtitle')}
                        </DirectionDescription>
                        <PrimaryAccordion
                            variant="light"
                            items={
                                activeDirection?.internshipTeams.map(
                                    (item) =>
                                        ({
                                            key: item.id,
                                            title: <span>{item.name}</span>,
                                            body: <DirectionTeam team={item} />,
                                        } as IAccordionItem)
                                ) || []
                            }
                        />
                    </>
                ),
                title: <span>{t('direction-teams')}</span>,
            },
        ],
        styles: {
            bodyStyles: `padding: 24px 0 0 0;`,
        },
    });

    if (!directions || directions.length === 0) {
        return null;
    }

    return (
        <DirectionsWrapper>
            <Bubble5 src={process.env.PUBLIC_URL + `/assets/images/bubble5.png`} alt="Safeboard bubble image" />
            <Bubble6 src={process.env.PUBLIC_URL + `/assets/images/bubble6.png`} alt="Safeboard bubble image" />

            <BaseWrapper styles={{ position: 'relative' }} id="directions">
                <Container>
                    {!!title && (
                        <TitleContainer>
                            <Title>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: sanitize(title),
                                    }}
                                />
                            </Title>
                        </TitleContainer>
                    )}
                    {!!description && (
                        <DescriptionContainer>
                            <Description
                                dangerouslySetInnerHTML={{
                                    __html: sanitize(description),
                                }}
                            />
                        </DescriptionContainer>
                    )}

                    <DesktopDirectionsContainer>
                        <DirectionsGrid>
                            {directions?.map((d) => (
                                <DirectionBlock
                                    key={d.id}
                                    item={d}
                                    isClosed={!!isClosed}
                                    click={() => handleDirectionClick(d)}
                                />
                            ))}
                        </DirectionsGrid>
                    </DesktopDirectionsContainer>

                    <MobileDirectionsContainer>
                        <PrimaryAccordion variant="dark" items={mobileAccordionItems} />
                    </MobileDirectionsContainer>
                </Container>
            </BaseWrapper>

            <PrimaryModal atSelector="directionsModalSelector" ref={modalRef}>
                {activeDirection && (
                    <>
                        <ModalTitle data-at-selector={'directionsModalTitle'} data-at-id={activeDirection.id}>
                            {activeDirection.name}
                        </ModalTitle>
                        <ModalContainer data-at-selector={'directionsModalContainer'}>
                            <Tab {...getTabs()} />
                        </ModalContainer>
                    </>
                )}
            </PrimaryModal>
        </DirectionsWrapper>
    );
};

export default Directions;
